"use client";

import { useSearch } from "@/context/SearchContext";
import { SearchStateManager, pickCollections } from "@/utils/search-state";
import { getSearchAutocomplete } from "@/app/api/client-only";
import { useDebounced } from "./hooks/useDebounced";
import Image from "next/image";
import ClearIcon from "./images/clear.svg";
import SearchIcon from "./images/input-search.svg";
import CollectionsTagsSelect from "./collections-tags-select";
import { Branding } from "@/types/branding";
import { useCallback, useEffect, useRef, useState } from "react";
import { AutocompleteSelect } from "../common/AutocompleteSelect/AutocompleteSelect";

interface SearchInputProps {
  searchPlaceholder: string;
  searchButtonText?: string;
  selectLibrariesPlaceholder?: string;
  clearButtonAriaLabel?: string;
  branding?: Branding | null;
}

export default function SearchInput({
  searchPlaceholder,
  searchButtonText,
  selectLibrariesPlaceholder,
  clearButtonAriaLabel,
  branding,
}: SearchInputProps) {
  const {
    search,
    currentSearchTerm,
    slug,
    setSearchResult,
    clearAll,
    filterState,
    updateCollections,
  } = useSearch();

  const [inputValue, setInputValue] = useState(currentSearchTerm ?? "");
  const debouncedValue = useDebounced(inputValue, 300);

  const showSearchSuggestions = branding?.show_search_suggestions === true;

  const [suggestions, setSuggestions] = useState<
    Array<{ value: string; id?: string }>
  >([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const skipNextAutocompleteFetchRef = useRef(false);
  const userHasTypedRef = useRef(false);

  useEffect(() => {
    userHasTypedRef.current = false;
    setInputValue(currentSearchTerm ?? "");
  }, [currentSearchTerm]);

  useEffect(() => {
    if (!showSearchSuggestions) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const term = debouncedValue.trim();
    if (skipNextAutocompleteFetchRef.current) {
      skipNextAutocompleteFetchRef.current = false;
      return;
    }
    if (!term) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await getSearchAutocomplete(slug, term);
        if (!cancelled) {
          setSuggestions(data.suggestions ?? []);
          const hasSuggestions = (data.suggestions?.length ?? 0) > 0;
          setShowSuggestions(hasSuggestions && userHasTypedRef.current);
        }
      } catch {
        if (!cancelled) {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showSearchSuggestions, debouncedValue, slug]);

  const executeSearch = useCallback(
    (
      term: string,
      options?: { source?: "autocomplete"; journalId?: string },
    ) => {
      setShowSuggestions(false);
      const trimmed = term.trim();
      if (trimmed) {
        // A new search resets all filters except collections (a special filter
        // that scopes which libraries to search and must persist).
        const preserved = pickCollections(filterState.selected);
        const stateManager = new SearchStateManager()
          .setTerm(trimmed)
          .setFilters(preserved);
        window.history.replaceState(null, "", stateManager.toURL(`/${slug}`));
        search(trimmed, preserved, options);
      } else {
        window.history.replaceState(null, "", `/${slug}`);
        setSearchResult(null);
      }
    },
    [slug, filterState.selected, search, setSearchResult],
  );

  const handleSuggestionSelect = useCallback(
    (item: { value: string; id?: string }) => {
      setInputValue(item.value);
      setShowSuggestions(false);
      skipNextAutocompleteFetchRef.current = true;
      executeSearch(item.value, {
        source: "autocomplete",
        journalId: item.id,
      });
    },
    [executeSearch],
  );

  const onClear = useCallback(() => {
    setInputValue("");
    setSuggestions([]);
    setShowSuggestions(false);
    clearAll();
  }, [clearAll]);

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        executeSearch(inputValue);
      }
    },
    [inputValue, executeSearch],
  );

  const handleInputChange = useCallback((value: string) => {
    userHasTypedRef.current = true;
    setInputValue(value);
  }, []);

  return (
    <div className="search-wrapper">
      <div className="input-container-wrapper">
        <div
          className="input-container input-container--autocomplete"
          style={{
            outlineColor: branding?.main_color,
          }}
        >
          <label htmlFor="searchInput" className="visually-hidden">
            Search
          </label>
          <AutocompleteSelect
            id="searchInput"
            value={inputValue}
            onChange={handleInputChange}
            placeholder={searchPlaceholder}
            options={suggestions}
            onSelectOption={handleSuggestionSelect}
            open={showSuggestions}
            onOpenChange={setShowSuggestions}
            className="autocomplete-select--search"
            listId="searchSuggestions"
            onKeyDown={handleInputKeyDown}
            renderTrailing={({ clear }) => (
              <button
                type="button"
                id="clearButton"
                onClick={() => {
                  clear();
                  onClear();
                }}
                aria-label={clearButtonAriaLabel || "Clear"}
                className="autocomplete-select__clear autocomplete-select__clear--search"
              >
                <Image
                  id="clearIcon"
                  src={ClearIcon}
                  alt=""
                  width={24}
                  height={24}
                />
              </button>
            )}
          />
        </div>
        <div id="searchButtonWrapper">
          <button
            type="button"
            id="searchButton"
            onClick={() => executeSearch(inputValue)}
            aria-label={searchButtonText || "Search"}
          >
            <Image id="searchButtonIcon" src={SearchIcon} alt="" fill />
          </button>
        </div>
      </div>
      {branding?.collections &&
        Object.keys(branding.collections).length > 0 && (
          <div className="collections-wrapper">
            <CollectionsTagsSelect
              branding={branding}
              collections={branding.collections}
              selectedCollections={filterState.selected.collections || []}
              onChange={updateCollections}
              placeholder={selectLibrariesPlaceholder ?? "Select libraries"}
              label="Filter by library collections"
            />
          </div>
        )}
    </div>
  );
}
